import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { SearchableSelectField } from "../../shared/SearchableSelectField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  title: "",
  description: "",
  code: "",
  discpuntType: "",
  discountValue: 0,
  minOrderAmount: 0,
  maxDiscountAmount: 0,
  usageLimit: 0,
  customerUsageLimit: 0,
  applicableType: "",
  applicableId: "",
  startDate: "",
  endDate: "",
  isActive: true,
  createdById: "",
  createdDate: "",
  updatedDate: ""
});

function asInputDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function couponToForm(x) {
  return {
    title: x.title ?? "",
    description: x.description ?? "",
    code: x.code ?? "",
    discpuntType: x.discpuntType ?? "",
    discountValue: x.discountValue ?? 0,
    minOrderAmount: x.minOrderAmount ?? 0,
    maxDiscountAmount: x.maxDiscountAmount ?? 0,
    usageLimit: x.usageLimit ?? 0,
    customerUsageLimit: x.customerUsageLimit ?? 0,
    applicableType: x.applicableType ?? "",
    applicableId: x.applicableId ?? "",
    startDate: asInputDateTime(x.startDate),
    endDate: asInputDateTime(x.endDate),
    isActive: Boolean(x.isActive),
    createdById: x.createdById ?? "",
    createdDate: asInputDateTime(x.createdDate),
    updatedDate: asInputDateTime(x.updatedDate)
  };
}

export function CouponsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const uRes = await apiGet("/api/Users");
      if (cancelled) return;
      if (uRes.ok) setUsers(uRes.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const res = await apiGet(`/api/Coupons/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load coupon");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm(couponToForm(x));
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.createdById) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/Coupons", {
      id: editingId ?? 0,
      title: form.title || null,
      description: form.description || null,
      code: form.code || null,
      discpuntType: form.discpuntType || null,
      discountValue: Number(form.discountValue || 0),
      minOrderAmount: Number(form.minOrderAmount || 0),
      maxDiscountAmount: Number(form.maxDiscountAmount || 0),
      usageLimit: Number(form.usageLimit || 0),
      customerUsageLimit: Number(form.customerUsageLimit || 0),
      applicableType: form.applicableType || null,
      applicableId: form.applicableId ? Number(form.applicableId) : null,
      startDate: form.startDate || new Date().toISOString(),
      endDate: form.endDate || new Date().toISOString(),
      isActive: Boolean(form.isActive),
      createdById: Number(form.createdById),
      createdDate: form.createdDate || new Date().toISOString(),
      updatedDate: form.updatedDate || null
    });
    if (!res.ok) setError(res.error ?? "Failed to save coupon");
    else resetForm();
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Coupons/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load coupon");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm(couponToForm(x));
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Coupon" : "Add Coupon"}</h2>
          <p>Manage discount coupons.</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="coupons-form"
            disabled={saving}
            title="Save"
            aria-label="Save coupon"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/coupons-list" className="icon-button icon-button--toolbar" title="Coupons list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <div className="card-body">
        <form id="coupons-form" onSubmit={onSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--2">
            <label className="field">
              <span>Title</span>
              <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
            </label>
            <label className="field">
              <span>Code</span>
              <input value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
            </label>
          </div>
        <label className="field">
          <span>Discount Type</span>
          <input value={form.discpuntType} onChange={(e) => setForm((p) => ({ ...p, discpuntType: e.target.value }))} />
        </label>
        <label className="field">
          <span>Discount Value</span>
          <input type="number" value={form.discountValue} onChange={(e) => setForm((p) => ({ ...p, discountValue: e.target.value }))} />
        </label>
        <label className="field">
          <span>Min Order Amount</span>
          <input type="number" value={form.minOrderAmount} onChange={(e) => setForm((p) => ({ ...p, minOrderAmount: e.target.value }))} />
        </label>
        <label className="field">
          <span>Max Discount Amount</span>
          <input type="number" value={form.maxDiscountAmount} onChange={(e) => setForm((p) => ({ ...p, maxDiscountAmount: e.target.value }))} />
        </label>
        <label className="field">
          <span>Usage Limit</span>
          <input type="number" value={form.usageLimit} onChange={(e) => setForm((p) => ({ ...p, usageLimit: e.target.value }))} />
        </label>
        <label className="field">
          <span>Customer Usage Limit</span>
          <input type="number" value={form.customerUsageLimit} onChange={(e) => setForm((p) => ({ ...p, customerUsageLimit: e.target.value }))} />
        </label>
        <label className="field">
          <span>Applicable Type</span>
          <input value={form.applicableType} onChange={(e) => setForm((p) => ({ ...p, applicableType: e.target.value }))} />
        </label>
        <label className="field">
          <span>Applicable Id</span>
          <input type="number" value={form.applicableId} onChange={(e) => setForm((p) => ({ ...p, applicableId: e.target.value }))} />
        </label>
        <label className="field">
          <span>Start Date</span>
          <input type="datetime-local" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />
        </label>
        <label className="field">
          <span>End Date</span>
          <input type="datetime-local" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} />
        </label>
        <SearchableSelectField
          label="Created By"
          options={users}
          value={form.createdById}
          onChange={(nextValue) => setForm((p) => ({ ...p, createdById: nextValue === "" ? "" : Number(nextValue) }))}
          getOptionLabel={(option) => option.userName || `User #${option.id}`}
          getSearchText={(option) => `${option.userName ?? ""} ${option.email ?? ""} ${option.id ?? ""}`}
          placeholder="Search user"
        />
        <label className="field">
          <span>Created Date</span>
          <input type="datetime-local" value={form.createdDate} onChange={(e) => setForm((p) => ({ ...p, createdDate: e.target.value }))} />
        </label>
        <label className="field">
          <span>Updated Date</span>
          <input type="datetime-local" value={form.updatedDate} onChange={(e) => setForm((p) => ({ ...p, updatedDate: e.target.value }))} />
        </label>
          <label className="field field--full">
            <span>Description</span>
            <textarea rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </label>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </div>
    </div>
  );
}
